import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import type { SilentRequest } from '@azure/msal-browser';
import { msalConfig, apiScopes, apiBaseUrl } from './authConfig';

export interface ApiTokenRequest {
  scopes: string[];
  account?: any;
}

export interface TokenDebugInfo {
  token: string;
  decoded: {
    header: any;
    payload: any;
  };
  validity: {
    isValid: boolean;
    isExpired: boolean;
    expiresAt: Date;
    issuedAt: Date;
    notBefore?: Date;
  };
  claims: {
    issuer: string;
    audience: string;
    subject: string;
    tokenVersion?: string;
    appId?: string;
    tenantId?: string;
    objectId?: string;
    preferredUsername?: string;
    name?: string;
    roles?: string[];
    scp?: string;
  };
}

class AuthService {
  private msalInstance: PublicClientApplication;
  private cachedToken: string | null = null;
  private tokenExpiry: number = 0;
  private isInitialized: boolean = false;

  constructor() {
    this.msalInstance = new PublicClientApplication(msalConfig);
    this.initialize();
  }

  private async initialize() {
    try {
      await this.msalInstance.initialize();
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize MSAL instance:', error);
    }
  }

  private async ensureInitialized() {
    if (!this.isInitialized) {
      await this.initialize();
    }
  }

  /**
   * Acquire access token silently for API calls (with API-specific scopes)
   * Now includes retry logic for better reliability
   */
  async getAccessToken(scopes: string[] = apiScopes, maxRetries: number = 5): Promise<string | null> {
    await this.ensureInitialized();

    // Return cached token if it's still valid
    if (this.cachedToken && this.tokenExpiry > Date.now()) {
      console.log('Using cached token', this.cachedToken);
      return this.cachedToken;
    }

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const accounts = this.msalInstance.getAllAccounts();

        if (accounts.length === 0) {
          console.warn(`Attempt ${attempt}/${maxRetries}: No accounts found. User needs to sign in.`);
          
          // If this is not the last attempt, wait before retrying
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            continue;
          }
          
          return null;
        }

        const silentRequest: SilentRequest = {
          account: accounts[0],
          scopes: scopes,
        };

        const response = await this.msalInstance.acquireTokenSilent(silentRequest);

        // Cache the token with expiry
        this.cacheToken(response.accessToken, response.expiresOn);
        return response.accessToken;

      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          console.warn(`Attempt ${attempt}/${maxRetries}: Silent token acquisition failed. Interactive login required.`);
          
          // Only try interactive login on the last attempt
          if (attempt === maxRetries) {
            try {
              // Fallback to redirect login (recommended for minimal setup)
              await this.msalInstance.acquireTokenRedirect({ scopes: scopes });
              return null; // redirect flow never returns here
            } catch (interactiveError) {
              console.error('Interactive token acquisition failed:', interactiveError);
              return null;
            }
          }
        } else {
          console.error(`Attempt ${attempt}/${maxRetries}: Token acquisition failed:`, error);
        }

        // If this is not the last attempt, wait before retrying
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * attempt, 5000); // Exponential backoff, max 5 seconds
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    console.error(`❌ Failed to acquire token after ${maxRetries} attempts`);
    return null;
  }

  /**
   * Sign in with redirect (minimal MSAL approach)
   */
  async signIn(): Promise<void> {
    await this.ensureInitialized();

    try {
      await this.msalInstance.loginRedirect({ scopes: apiScopes });
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  /**
   * Cache token with expiry time
   */
  private cacheToken(token: string, expiresOn: Date | null) {
    this.cachedToken = token;
    // Set expiry 5 minutes before actual expiry for safety
    this.tokenExpiry = expiresOn ? expiresOn.getTime() - (5 * 60 * 1000) : Date.now() + (55 * 60 * 1000);
    // Debug token after caching
    this.debugToken(token);
  }

  /**
   * Decode and debug JWT token
   */
  debugToken(token: string): TokenDebugInfo | null {
    try {
      const debugInfo = this.decodeAndValidateToken(token);
      return debugInfo;
    } catch (error) {
      console.error('❌ Token debug failed:', error);
      return null;
    }
  }

  /**
   * Decode JWT token and validate
   */
  private decodeAndValidateToken(token: string): TokenDebugInfo {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT token format');
    }

    // Decode header and payload
    const header = JSON.parse(atob(parts[0]));
    const payload = JSON.parse(atob(parts[1]));

    // Extract validity information
    const now = Math.floor(Date.now() / 1000);
    const exp = payload.exp;
    const iat = payload.iat;
    const nbf = payload.nbf;

    const validity = {
      isValid: exp > now,
      isExpired: exp <= now,
      expiresAt: new Date(exp * 1000),
      issuedAt: new Date(iat * 1000),
      notBefore: nbf ? new Date(nbf * 1000) : undefined
    };

    // Extract claims
    const claims = {
      issuer: payload.iss || '',
      audience: payload.aud || '',
      subject: payload.sub || '',
      tokenVersion: payload.ver || payload.version,
      appId: payload.appid || payload.azp,
      tenantId: payload.tid,
      objectId: payload.oid,
      preferredUsername: payload.preferred_username,
      name: payload.name,
      roles: payload.roles,
      scp: payload.scp
    };

    return {
      token,
      decoded: { header, payload },
      validity,
      claims
    };
  }

  /**
   * Clear cached token
   */
  clearTokenCache() {
    this.cachedToken = null;
    this.tokenExpiry = 0;
  }

  /**
   * Get current user account
   */
  getCurrentAccount() {
    if (!this.isInitialized) return null;
    const accounts = this.msalInstance.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    if (!this.isInitialized) return false;
    return this.msalInstance.getAllAccounts().length > 0;
  }

  /**
   * Sign out user
   */
  async signOut() {
    await this.ensureInitialized();

    try {
      // Clear cached token before logout
      this.clearTokenCache();

      await this.msalInstance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }

  /**
   * Get user info from current account
   */
  getUserInfo() {
    const account = this.getCurrentAccount();
    if (!account) return null;

    return {
      name: account.name,
      username: account.username,
      email: account.username,
      id: account.localAccountId,
    };
  }

  /**
   * Create authenticated fetch request for your API
   * Now includes retry logic for token acquisition
   */
  async authenticatedFetch(url: string, options: RequestInit = {}, scopes: string[] = apiScopes): Promise<Response> {
    // Try to get token with retry logic
    const token = await this.getAccessToken(scopes, 5);

    if (!token) {
      console.error('❌ Unable to acquire access token after retries');
      throw new Error('Unable to acquire access token');
    }

    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Use full URL or prepend apiBaseUrl if relative
    const fullUrl = `${url}`;
    return fetch(fullUrl, {
      ...options,
      headers,
    });
  }

  /**
   * Get current token debug information
   */
  async getCurrentTokenDebugInfo(scopes: string[] = apiScopes): Promise<TokenDebugInfo | null> {
    const token = await this.getAccessToken(scopes, 3); // Use 3 retries for debug
    if (!token) return null;
    console.log('Debugging current token:', token);
    return this.decodeAndValidateToken(token);
  }

  /**
   * Debug any JWT token (public method)
   */
  public debugAnyToken(token: string): TokenDebugInfo | null {
    return this.debugToken(token);
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;